import "../styles/bigCalendar.css";
import moment from "moment";
import React, {
    useState,
    useCallback,
    useMemo,
    useEffect,
    cloneElement,
    Children,
} from "react";
import PropTypes from "prop-types";
import {
    Calendar,
    Views,
    DateLocalizer,
    Event,
    momentLocalizer,
} from "react-big-calendar";
import * as firestore from "firebase/firestore";
import styles from "../styles/Home.module.css";
import PromptModal from "./PromptModal";
import ConfirmModal from "./ConfirmModal";
import { firestore as db } from "../firebase";

const localizer = momentLocalizer(moment);

const ClassCalendar = ({
    page,
    isAdmin,
}: {
    page: string;
    isAdmin: boolean;
}) => {
    const [myEvents, setEvents] = useState<Event[]>([]);
    const [lastPage, setLastPage] = useState(page);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalValue, setModalValue] = useState("");
    const [pendingSlot, setPendingSlot] = useState<{ start: Date; end: Date } | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

    // Fetch events from Firestore on mount and page change
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                console.log("Fetching events for page:", page);
                const calendarRef = firestore.doc(db, "calendars", page);
                const docSnapshot = await firestore.getDoc(calendarRef);

                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    if (data?.data) {
                        const parsedEvents = JSON.parse(data.data);
                        const validEvents = parsedEvents.map((event: any) => ({
                            ...event,
                            start: new Date(event.start),
                            end: new Date(event.end),
                        }));
                        console.log("Fetched events:", validEvents);
                        setEvents(validEvents);
                    } else {
                        console.log("No events data in document");
                        setEvents([]);
                    }
                } else {
                    console.log("Creating new document for page:", page);
                    // Initialize the document if it doesn't exist
                    await firestore.setDoc(calendarRef, {
                        name: page,
                        data: JSON.stringify([]),
                    });
                    setEvents([]);
                }
                setLastPage(page);
            } catch (error) {
                console.error("Error in fetchEvents:", error);
            }
        };

        fetchEvents();
    }, [page]);

    // Sync events to Firestore
    const syncEventsToFirebase = useCallback(
        async (eventsToSync: Event[]) => {
            if (page !== lastPage) {
                console.log("Page changed, preventing sync");
                return;
            }

            try {
                console.log(
                    "Starting sync for page:",
                    page,
                    "events:",
                    eventsToSync
                );
                const calendarRef = firestore.doc(db, "calendars", page);

                // Check if document exists first
                const docSnapshot = await firestore.getDoc(calendarRef);

                if (docSnapshot.exists()) {
                    await firestore.updateDoc(calendarRef, {
                        name: page,
                        data: JSON.stringify(eventsToSync),
                    });
                } else {
                    await firestore.setDoc(calendarRef, {
                        name: page,
                        data: JSON.stringify(eventsToSync),
                    });
                }
                console.log("Sync completed successfully");
            } catch (error) {
                console.error("Error in syncEventsToFirebase:", error);
            }
        },
        [page, lastPage]
    );

    const handleSelectSlot = useCallback(
        ({ start, end }: { start: Date; end: Date }) => {
            if (isAdmin && page === lastPage) {
                setPendingSlot({ start, end });
                setModalValue("");
                setModalOpen(true);
            }
        },
        [isAdmin, page, lastPage]
    );

    const handleModalSubmit = useCallback(() => {
        if (pendingSlot && modalValue.trim()) {
            setEvents((prev) => {
                const newEvents = [...prev, { start: pendingSlot.start, end: pendingSlot.end, title: modalValue.trim() }];
                syncEventsToFirebase(newEvents);
                return newEvents;
            });
        }
        setModalOpen(false);
        setModalValue("");
        setPendingSlot(null);
    }, [pendingSlot, modalValue, syncEventsToFirebase]);

    const handleModalCancel = useCallback(() => {
        setModalOpen(false);
        setModalValue("");
        setPendingSlot(null);
    }, []);

    const handleSelectEvent = useCallback(
        (event: Event) => {
            if (isAdmin && page === lastPage) {
                setEventToDelete(event);
                setConfirmOpen(true);
            }
        },
        [isAdmin, page, lastPage]
    );

    const handleConfirmDelete = useCallback(() => {
        if (eventToDelete) {
            setEvents((prev) => {
                const updatedEvents = prev.filter((i) => i !== eventToDelete);
                syncEventsToFirebase(updatedEvents);
                return updatedEvents;
            });
        }
        setConfirmOpen(false);
        setEventToDelete(null);
    }, [eventToDelete, syncEventsToFirebase]);

    const handleCancelDelete = useCallback(() => {
        setConfirmOpen(false);
        setEventToDelete(null);
    }, []);

    // Only sync on events change if we're on the right page
    useEffect(() => {
        if (page === lastPage) {
            syncEventsToFirebase(myEvents);
        }
    }, [myEvents, syncEventsToFirebase]);

    const { defaultDate, scrollToTime } = useMemo(
        () => ({
            defaultDate: new Date(),
            scrollToTime: new Date(1970, 1, 1, 6),
        }),
        []
    );

    // const TouchCellWrapper: React.FC<any> = ({
    //     children,
    //     value,
    //     onSelectSlot,
    // }) =>
    //     cloneElement(Children.only(children), {
    //         onTouchEnd: () => onSelectSlot({ action: "click", slots: [value] }),
    //         style: {
    //             className: `${children}`,
    //         },
    //     });

    return (
        <div
            className={isAdmin ? "adminCalendar" : "userCalendar"}
            style={{ height: "100%", width: "100%" }}
        >
            <Calendar
                defaultDate={defaultDate}
                defaultView={Views.WEEK}
                events={myEvents}
                localizer={localizer}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable={true}
                scrollToTime={scrollToTime}
                style={{ height: "100%", width: "100%" }}
                messages={{
                    next: "arrow_forward",
                    previous: "arrow_back",
                }}
                views={{
                    month: true,
                    week: true,
                    day: false,
                }}
            />
            <PromptModal
                isOpen={modalOpen}
                title="Add Calendar Event"
                label="Event Name"
                value={modalValue}
                onChange={setModalValue}
                onSubmit={handleModalSubmit}
                onCancel={handleModalCancel}
            />
            <ConfirmModal
                isOpen={confirmOpen}
                title="Delete Event"
                message="Are you sure you want to delete this event?"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
        </div>
    );
};

export default ClassCalendar;
